/**
 * The reveal gate (V5-04) — password re-authentication before a masked number
 * is shown.
 *
 * WHY A PASSWORD AND NOT A FINGERPRINT
 * -------------------------------------
 * The requirement says "biometric reveal". A browser SPA cannot do that, and
 * pretending otherwise would be the worst outcome — a gate everyone believes
 * is strong and isn't.
 *
 * What a browser offers is WebAuthn, and WebAuthn is not a "confirm it's you"
 * API. It asserts a *registered credential*: the user must first enrol a
 * passkey, the server must store its public key, and the assertion must be
 * verified against that key. AIM has no credential store, no registration
 * flow, and no server-side WebAuthn verifier — building all three is the V5-05
 * MFA requirement, which is a separate piece of work. Calling
 * `navigator.credentials.get()` with nothing registered fails; calling it and
 * ignoring the result is theatre.
 *
 * So the factor here is the ACCOUNT PASSWORD, checked server-side against the
 * same bcrypt hash `POST /auth/login` checks. That is a real re-authentication:
 * it cannot be satisfied by holding the session, which is exactly the threat —
 * an unlocked phone on a table already has a valid session. The native client
 * can put a device biometric in FRONT of this call later without changing a
 * line of the server contract.
 *
 * THE GRANT LIVES IN MEMORY, AND ONLY IN MEMORY
 * ----------------------------------------------
 * Never `localStorage`, never a cookie. A grant that survives a reload is a
 * grant that survives the phone changing hands, which defeats the point. It is
 * also cleared on sign-out and expires on the server's clock regardless of
 * what this module believes.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Eye, Lock } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import { RevealRejected, requestRevealGrant } from "./api";
import { getPrivacy } from "./store";

interface Grant {
  token: string;
  /** Epoch millis, derived from the SERVER's `expiresAt`. */
  expiresAtMs: number;
}

let grant: Grant | null = null;
const listeners = new Set<() => void>();
let timer: ReturnType<typeof setTimeout> | null = null;

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Whether reveals are currently unlocked.
 *
 * Checks the clock on every read rather than trusting the timer alone — a
 * backgrounded tab does not fire timers reliably, and "the timer has not run
 * yet" must never be the reason a number stays visible past its grant.
 */
export function isRevealed(): boolean {
  if (!grant) return false;
  if (Date.now() >= grant.expiresAtMs) {
    grant = null;
    return false;
  }
  return true;
}

function scheduleExpiry(atMs: number): void {
  if (timer) clearTimeout(timer);
  const delay = Math.max(0, atMs - Date.now());
  timer = setTimeout(() => {
    grant = null;
    timer = null;
    emit();
  }, delay);
}

function grantFromServer(token: string, expiresAt: string, expiresIn: number): void {
  // Prefer the server's absolute timestamp; fall back to its TTL if the string
  // is unparseable, so a clock-format surprise degrades to a shorter window
  // rather than an immediately-expired one.
  const parsed = Date.parse(expiresAt);
  const expiresAtMs = Number.isNaN(parsed) ? Date.now() + expiresIn * 1000 : parsed;
  grant = { token, expiresAtMs };
  scheduleExpiry(expiresAtMs);
  emit();
}

/** Drop any grant. Called on sign-out and by the "lock now" affordance. */
export function clearReveal(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  grant = null;
  emit();
}

/** Live "are reveals unlocked" for components. */
export function useRevealed(): boolean {
  return useSyncExternalStore(subscribe, isRevealed, () => false);
}

/**
 * Unlock without a prompt when the user has turned the password requirement
 * off. `revealRequiresPassword: false` is a real reduction in protection and
 * is offered only because a single-user desktop session is a legitimate threat
 * model too — the setting screen says so in as many words.
 */
function grantWithoutPassword(): void {
  const seconds = getPrivacy().revealTimeoutSeconds;
  const expiresAtMs = Date.now() + seconds * 1000;
  grant = { token: "", expiresAtMs };
  scheduleExpiry(expiresAtMs);
  emit();
}

/**
 * The hook a reveal affordance uses.
 *
 * `request()` either unlocks immediately (password not required, or a grant is
 * still live) or opens the dialog. The dialog is rendered by the caller via
 * `<RevealDialog {...dialogProps} />` so the prompt appears once per trigger
 * rather than once per masked field on the screen.
 */
export function useRevealGate() {
  const revealed = useRevealed();
  const [open, setOpen] = useState(false);

  const request = useCallback(() => {
    if (isRevealed()) return;
    if (!getPrivacy().revealRequiresPassword) {
      grantWithoutPassword();
      return;
    }
    setOpen(true);
  }, []);

  return {
    revealed,
    request,
    lock: clearReveal,
    dialogProps: { open, onOpenChange: setOpen },
  };
}

/**
 * The prompt itself.
 *
 * `type="password"` with `autoComplete="current-password"` so a password
 * manager fills it, and `autoFocus` so the keyboard is already there on a
 * phone. The field is cleared on every close — including a successful one —
 * because a password left in a React state tree is a password in a heap dump.
 */
export function RevealDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setPassword("");
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await requestRevealGrant(password);
      grantFromServer(result.token, result.expiresAt, result.expiresIn);
      setPassword("");
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof RevealRejected
          ? "That password did not match."
          : "Could not verify right now. Check your connection and try again.",
      );
      setBusy(false);
      inputRef.current?.select();
    }
  };

  const seconds = getPrivacy().revealTimeoutSeconds;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Confirm it's you"
      description={`Enter your AIM password to show hidden numbers for the next ${seconds} seconds.`}
      width="sm"
    >
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label htmlFor="reveal-password" required>
            Password
          </Label>
          <Input
            id="reveal-password"
            ref={inputRef}
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Your AIM password"
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? "reveal-error" : undefined}
          />
        </div>

        {error && (
          <p id="reveal-error" role="alert" className="text-xs text-[var(--cp-rose-ink)]">
            {error}
          </p>
        )}

        <p className="flex items-start gap-1.5 text-[11.5px] leading-snug text-[var(--cp-ink-3)]">
          <Lock className="mt-[1px] h-3.5 w-3.5 shrink-0" />
          Holding a signed-in session isn't enough on its own — that's the point
          of asking again here.
        </p>

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" loading={busy} disabled={!password}>
            <Eye className="h-4 w-4" />
            Show
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
